import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PdfUtilsServiceV2 } from '@src/app/core/services/helpers/pdf-utils/pdf-utils-v2.service';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import {
  DerivativeOperationDemandWithMtmDto,
  MTMOperationDetailDto,
} from '@src/app/core/services/http/derivatives/mtm/derivatives-mtm.service';
import ModalComexOperationDetailsComponent from '@src/app/features/dashboard/comex/pages/modal-comex-operation-details/modal-comex-operation-details.component';
import { ModalLoanDetailsComponent } from '@src/app/features/dashboard/costs/debt-management/pages/modal-loan-details/modal-loan-details.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { SortEvent } from '@src/app/shared/components/table/table.component';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import ExcelJS from 'exceljs';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-derivative-mtm-results-modal',
  imports: [
    CommonModule,
    SharedModule,
    ModalComexOperationDetailsComponent,
    ModalLoanDetailsComponent,
  ],
  templateUrl: './derivative-mtm-results.modal.html',
  styleUrl: './derivative-mtm-results.modal.scss',
})
export default class DerivativeMtmResultsModalComponent {
  @ViewChild('dialog') dialog!: DialogComponent;
  @ViewChild('comexDetailsModal') comexDetailsModal!: ModalComexOperationDetailsComponent;
  @ViewChild('loanDetailsModal') loanDetailsModal!: ModalLoanDetailsComponent;
  @Output() modalClosed = new EventEmitter<void>();

  @Input() data: DerivativeOperationDemandWithMtmDto | null = null;
  companyId: string = '';
  menuItems: MenuItem[] = [];

  // Controle de ordenação
  private sortedNdfCapItems: MTMOperationDetailDto[] = [];
  private sortedNdfVanillaItems: MTMOperationDetailDto[] = [];
  private sortedOpcoesItems: MTMOperationDetailDto[] = [];

  // Colunas das tabelas (padrão app-table)
  columnsCommon: ColumnDefinition[] = [
    new ColumnDefinition({
      field: 'idReferencia',
      header: 'Id Referência',
      template: 'idReferencia',
      sortField: 'idReferencia',
    }),
    new ColumnDefinition({
      field: 'origemOperacao',
      header: 'Origem da Operação',
      template: 'origemOperacao',
      sortField: 'origemOperacao',
    }),
    new ColumnDefinition({
      field: 'dataNegociacao',
      header: 'Data Negociação',
      format: 'date',
      sortField: 'dataNegociacao',
    }),
    new ColumnDefinition({
      field: 'dataVencimento',
      header: 'Data Vencimento',
      format: 'date',
      sortField: 'dataVencimento',
    }),
    new ColumnDefinition({ field: 'indexador', header: 'Indexador', sortField: 'indexador' }),
    new ColumnDefinition({
      field: 'forward',
      header: 'Forward',
      sortField: 'forward',
      format: 'currency:4',
    }),
    new ColumnDefinition({
      field: 'cap',
      header: 'CAP',
      sortField: 'cap',
      format: 'currency:4',
    }),
    new ColumnDefinition({
      field: 'notionalRound',
      header: 'Notional',
      sortField: 'notionalRound',
    }),
    new ColumnDefinition({
      field: 'notionalXReferenciaRound',
      header: 'Notional x Referência',
      format: 'currency:2',
      sortField: 'notionalXReferenciaRound',
    }),
    new ColumnDefinition({
      field: 'taxaMercadoRound',
      header: 'Taxa de Mercado',
      sortField: 'taxaMercadoRound',
      format: 'currency:4',
    }),
    new ColumnDefinition({
      field: 'valorMercadoRound',
      header: 'Valor a Mercado (BRL)',
      sortField: 'valorMercadoRound',
    }),
    new ColumnDefinition({
      field: 'saldoMTMRound',
      header: 'MTM (BRL)',
      sortField: 'saldoMTMRound',
    }),
  ];

  ndfCapColumns: ColumnDefinition[] = [...this.columnsCommon];

  ndfVanillaColumns: ColumnDefinition[] = [...this.columnsCommon];

  opcoesColumns: ColumnDefinition[] = [...this.columnsCommon];

  constructor(
    private readonly router: Router,
    private readonly pdfUtilsService: PdfUtilsServiceV2,
    private readonly toast: ToastHelperService
  ) {
    this.menuItems = [
      {
        label: 'Exportar PDF',
        icon: 'pi pi-download',
        command: () => this.exportToPdf(),
      },
      {
        label: 'Exportar Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportToExcel(),
      },
    ];
  }

  open(payload: DerivativeOperationDemandWithMtmDto, companyId: string): void {
    this.data = payload;
    this.companyId = companyId;

    // Reset sorting when new data is loaded
    this.sortedNdfCapItems = [];
    this.sortedNdfVanillaItems = [];
    this.sortedOpcoesItems = [];

    // Ajusta cabeçalho do Notional com a moeda estrangeira (extraída do Indexador)
    const capCurrency = this.getCurrencyFromOperations(this.data?.ndfcap?.operations || []);
    const vanillaCurrency = this.getCurrencyFromOperations(this.data?.ndfVanilla?.operations || []);
    const opcoesCurrency = this.getCurrencyFromOperations(this.data?.opcoes?.operations || []);

    this.ndfCapColumns = this.columnsCommon.map(col => {
      if (col.field === 'notionalRound' && capCurrency) {
        return new ColumnDefinition({
          ...col,
          header: `Notional (${capCurrency})`,
        });
      }
      return col;
    });

    this.ndfVanillaColumns = this.columnsCommon.map(col => {
      if (col.field === 'notionalRound' && vanillaCurrency) {
        return new ColumnDefinition({
          ...col,
          header: `Notional (${vanillaCurrency})`,
        });
      }
      return col;
    });

    this.opcoesColumns = this.columnsCommon.map(col => {
      if (col.field === 'notionalRound' && opcoesCurrency) {
        return new ColumnDefinition({
          ...col,
          header: `Notional (${opcoesCurrency})`,
        });
      }
      return col;
    });

    this.dialog?.open('', '', '', 'Fechar', '', 'modal-derivative-mtm-results', false, false);
  }

  close(): void {
    this.dialog?.close();
    this.modalClosed.emit();
  }

  getNdfCapItems(): MTMOperationDetailDto[] {
    if (this.sortedNdfCapItems.length > 0) {
      return this.sortedNdfCapItems;
    }
    return this.data?.ndfcap?.operations || [];
  }

  navigateToDerivative(item: MTMOperationDetailDto): void {
    // Navega para a lista de operações de derivativos filtrando pelo ID de referência
    // mesmo padrão usado na listagem (filter=sequentialId)
    this.router.navigate(['/dashboard/ndf-operation/list'], {
      queryParams: { filter: item.idReferencia, companyId: this.companyId },
    });
  }

  openOriginDetails(item: MTMOperationDetailDto): void {
    if (item?.comexOperationId) {
      this.comexDetailsModal?.open(item.comexOperationId);
      return;
    }
    if (item?.loanOperationId) {
      this.loanDetailsModal?.open(item.loanOperationId);
      return;
    }
  }

  getNdfVanillaItems(): MTMOperationDetailDto[] {
    if (this.sortedNdfVanillaItems.length > 0) {
      return this.sortedNdfVanillaItems;
    }
    return this.data?.ndfVanilla?.operations || [];
  }

  getOpcoesItems(): MTMOperationDetailDto[] {
    if (this.sortedOpcoesItems.length > 0) {
      return this.sortedOpcoesItems;
    }
    return this.data?.opcoes?.operations || [];
  }

  getCapTotal(): number {
    return this.data?.ndfcap?.totalMTM ?? 0;
  }

  getVanillaTotal(): number {
    return this.data?.ndfVanilla?.totalMTM ?? 0;
  }

  getOpcoesTotal(): number {
    return this.data?.opcoes?.totalMTM ?? 0;
  }

  handleTableSort(event: SortEvent, tableType: 'ndfCap' | 'ndfVanilla' | 'opcoes'): void {
    const { field, order } = event;

    if (!field || !order) {
      // Reset sorting
      if (tableType === 'ndfCap') {
        this.sortedNdfCapItems = [];
      } else if (tableType === 'ndfVanilla') {
        this.sortedNdfVanillaItems = [];
      } else {
        this.sortedOpcoesItems = [];
      }
      return;
    }

    const isAscending = order === 1;
    let items: MTMOperationDetailDto[] = [];

    if (tableType === 'ndfCap') {
      items = [...(this.data?.ndfcap?.operations || [])];
    } else if (tableType === 'ndfVanilla') {
      items = [...(this.data?.ndfVanilla?.operations || [])];
    } else {
      items = [...(this.data?.opcoes?.operations || [])];
    }

    // Encontrar a definição da coluna para obter o campo correto para ordenação
    const columnDef = this.columnsCommon.find(col => col.header === field);
    const sortField = columnDef?.sortField || columnDef?.field || field;

    // Ordenar os itens
    const sortedItems = items.sort((a, b) => {
      const aValue = this.getFieldValue(a, sortField);
      const bValue = this.getFieldValue(b, sortField);

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return isAscending
          ? aValue.localeCompare(bValue, 'pt-BR', { numeric: true })
          : bValue.localeCompare(aValue, 'pt-BR', { numeric: true });
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return isAscending ? aValue - bValue : bValue - aValue;
      }

      // Para datas
      if (aValue instanceof Date && bValue instanceof Date) {
        return isAscending
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Fallback para string
      const aStr = String(aValue);
      const bStr = String(bValue);
      return isAscending
        ? aStr.localeCompare(bStr, 'pt-BR', { numeric: true })
        : bStr.localeCompare(aStr, 'pt-BR', { numeric: true });
    });

    // Armazenar os itens ordenados
    if (tableType === 'ndfCap') {
      this.sortedNdfCapItems = sortedItems;
    } else if (tableType === 'ndfVanilla') {
      this.sortedNdfVanillaItems = sortedItems;
    } else {
      this.sortedOpcoesItems = sortedItems;
    }
  }

  private getFieldValue(item: MTMOperationDetailDto, field: string): unknown {
    // Acessar propriedades aninhadas usando notação de ponto
    const keys = field.split('.');
    let result: unknown = item;

    for (const key of keys) {
      if (result && typeof result === 'object' && key in result) {
        result = (result as Record<string, unknown>)[key];
      } else {
        return undefined;
      }
    }

    return result;
  }

  private extractForeignCurrency(indexador: string | undefined | null): string | null {
    if (!indexador || typeof indexador !== 'string') return null;
    const idx = indexador.toUpperCase().indexOf('X');
    if (idx > 0) {
      return indexador.substring(0, idx).trim();
    }
    return null;
  }

  private getCurrencyFromOperations(operations: MTMOperationDetailDto[]): string | null {
    for (const op of operations) {
      const cur = this.extractForeignCurrency(op.indexador);
      if (cur) return cur;
    }
    return null;
  }

  exportToPdf(): void {
    if (!this.data) {
      this.toast.showToastError('Erro', 'Sem dados para exportar');
      return;
    }

    const filename = `MTM_Resultados.pdf`;
    this.pdfUtilsService
      .exportToPdf({
        className: '.mtm-results',
        filename,
        title: 'Mark to Market (MTM)',
        subtitle: '',
      })
      .then(() => this.toast.showToastSuccess('Sucesso', 'PDF exportado com sucesso'))
      .catch(() => this.toast.showToastError('Erro', 'Erro ao exportar PDF'));
  }

  async exportToExcel(): Promise<void> {
    if (!this.data) {
      this.toast.showToastError('Erro', 'Sem dados para exportar');
      return;
    }

    try {
      const capItems = this.getNdfCapItems();
      const vanillaItems = this.getNdfVanillaItems();
      const opcoesItems = this.getOpcoesItems();

      const capCurrency = this.getCurrencyFromOperations(capItems) || undefined;
      const vanillaCurrency = this.getCurrencyFromOperations(vanillaItems) || undefined;
      const opcoesCurrency = this.getCurrencyFromOperations(opcoesItems) || undefined;

      const buildRow = (item: MTMOperationDetailDto, notionalHeader: string) => {
        const rowAny = item as unknown as Record<string, unknown>;
        const pickNumber = (roundedKey: string, base: number | null | undefined) => {
          const value = rowAny[roundedKey] as number | undefined;
          return value !== undefined ? value : (base ?? '');
        };

        const notional = pickNumber('notionalRound', item.notional);
        const notionalXReferencia = pickNumber(
          'notionalXReferenciaRound',
          item.notionalXReferencia
        );
        const taxaMercado = pickNumber('taxaMercadoRound', item.taxaMercado);
        const valorMercado = pickNumber('valorMercadoRound', item.valorMercado);
        const saldoMtm = pickNumber('saldoMTMRound', item.saldoMtm);

        const row: Record<string, unknown> = {
          'Id Referência': item.idReferencia,
          'Origem da Operação': item.origemOperacao,
          'Data Negociação': item.dataNegociacao,
          'Data Vencimento': item.dataVencimento,
          Indexador: item.indexador,
          Forward: item.forward ?? '',
          CAP: item.cap ?? '',
          // Notional header será dinâmico por planilha
          [notionalHeader]: notional,
          'Notional x Referência': notionalXReferencia,
          'Taxa de Mercado': taxaMercado,
          'Valor a Mercado': valorMercado,
          'MTM (BRL)': saldoMtm,
        };
        return row;
      };

      const capNotionalHeader = capCurrency ? `Notional (${capCurrency})` : 'Notional';
      const vanillaNotionalHeader = vanillaCurrency ? `Notional (${vanillaCurrency})` : 'Notional';
      const opcoesNotionalHeader = opcoesCurrency ? `Notional (${opcoesCurrency})` : 'Notional';

      const workbook = new ExcelJS.Workbook();

      // Adiciona abas apenas se tiver dados
      if (capItems.length > 0) {
        const capRows = capItems.map(item => buildRow(item, capNotionalHeader));
        const capWorksheet = workbook.addWorksheet('NDF CAP');
        if (capRows.length > 0) {
          const headers = Object.keys(capRows[0]);
          capWorksheet.addRow(headers);
          capRows.forEach(row => {
            capWorksheet.addRow(headers.map(key => row[key]));
          });
        }
      }

      if (vanillaItems.length > 0) {
        const vanillaRows = vanillaItems.map(item => buildRow(item, vanillaNotionalHeader));
        const vanillaWorksheet = workbook.addWorksheet('NDF Vanilla');
        if (vanillaRows.length > 0) {
          const headers = Object.keys(vanillaRows[0]);
          vanillaWorksheet.addRow(headers);
          vanillaRows.forEach(row => {
            vanillaWorksheet.addRow(headers.map(key => row[key]));
          });
        }
      }

      if (opcoesItems.length > 0) {
        const opcoesRows = opcoesItems.map(item => buildRow(item, opcoesNotionalHeader));
        const opcoesWorksheet = workbook.addWorksheet('Call Spread');
        if (opcoesRows.length > 0) {
          const headers = Object.keys(opcoesRows[0]);
          opcoesWorksheet.addRow(headers);
          opcoesRows.forEach(row => {
            opcoesWorksheet.addRow(headers.map(key => row[key]));
          });
        }
      }

      // Se não tiver nenhuma aba, cria uma vazia
      if (workbook.worksheets.length === 0) {
        workbook.addWorksheet('NDF CAP');
        workbook.addWorksheet('NDF Vanilla');
        workbook.addWorksheet('Call Spread');
      }

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'MTM_Resultados.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);

      this.toast.showToastSuccess('Sucesso', 'Excel exportado com sucesso');
    } catch {
      this.toast.showToastError('Erro', 'Erro ao exportar Excel');
    }
  }
}
